import { useBatchI18n } from '@cvsdigital_web/react-cmk-i18n';
import { PERSONAL_INFO_TRANSLATION_CONFIG, processPersonalInfoTranslations } from '../constants/personalInfo';
import {
  processRegistrationBlockedTranslations,
  REGISTRATION_BLOCKED_TRANSLATION_CONFIG
} from '../constants/registrationBlocked';
import { DEMOGRAPHIC_TRANSLATION_CONFIG, processDemographicTranslations } from '../constants/demographic';
import { IDENTITY_INFO_TRANSLATION_CONFIG, processIdentityInfoTranslations } from '../constants/identityInfo';
import {
  PHONE_VERIFICATION_TRANSLATION_CONFIG,
  processPhoneVerificationTranslations
} from '../constants/phoneVerification';
import { ADULT_MINOR_TRANSLATION_CONFIG, processAdultMinorTranslations } from '../constants/adultMinor';
import { ALREADY_LINKED_TRANSLATION_CONFIG, processAlreadyLinkedTranslations } from '../constants/alreadyLinked';
import { ALREADY_EXISTS_TRANSLATION_CONFIG, processAlreadyExistsTranslations } from '../constants/alreadyExists';
import { CONFIRMATION_TRANSLATION_CONFIG, processConfirmationTranslations } from '../constants/confirmation';
import {
  CONNECT_ANOTHER_WAY_TRANSLATION_CONFIG,
  processConnectAnotherWayTranslations
} from '../constants/connectAnotherWay';
import { LOCKED_TRANSLATION_CONFIG, processLockedTranslations } from '../constants/locked';
import { LOCKOUT_TRANSLATION_CONFIG, processLockoutTranslations } from '../constants/lockout';
import { processSuccessTranslations, SUCCESS_TRANSLATION_CONFIG } from '../constants/success';
import { PHONE_DOB_TRANSLATION_CONFIG, processPhoneDobTranslations } from '../constants/phoneDob';
import { processRxLabelTranslations, RXLABEL_TRANSLATION_CONFIG } from '../constants/rxLabel';
import { MFA_TRANSLATION_CONFIG, processMfaTranslations } from '../constants/mfa';
import { ERROR_PAGE_TRANSLATION_CONFIG, processErrorPageTranslations } from '../constants/errorPage';
import { CSR_TRANSLATION_CONFIG, processCsrTranslations } from '../constants/csr';
import { LOOKUP_ERRORS_TRANSLATION_CONFIG, processLookupErrorsTranslations } from '../constants/lookupErrors';
import { LOB_FOOTER_TRANSLATION_CONFIG, processLobFooterTranslations } from '../constants/lobFooter';

// Safe wrapper for useBatchI18n to handle i18n package failures
const useSafeBatchI18n = (config: any[], namespace: string) => {
  try {
    return useBatchI18n(config, namespace) || {};
  } catch (error) {
    console.error(`i18n package failed for namespace: ${namespace}`, error);
    return {};
  }
};

// Generic translation hook creator
const createTranslationHook = <T>(
  translationConfig: any[],
  namespace: string,
  processor: (translatedItems: any) => T | null
) => {
  return (): T | null => {
    const translatedItems = useSafeBatchI18n(translationConfig, namespace);
    return processor(translatedItems);
  };
};

// Hook to handle personal info page translations
export const usePersonalInfoTranslations = createTranslationHook(
  PERSONAL_INFO_TRANSLATION_CONFIG,
  'personal-info-page',
  processPersonalInfoTranslations
);

// Hook to handle demographic page translations
export const useDemographicTranslations = createTranslationHook(
  DEMOGRAPHIC_TRANSLATION_CONFIG,
  'demographic-page',
  processDemographicTranslations
);

// Hook to handle identity-info page translations
export const useIdentityInfoTranslations = createTranslationHook(
  IDENTITY_INFO_TRANSLATION_CONFIG,
  'identity-info-page',
  processIdentityInfoTranslations
);

// Hook to handle registration-blocked page translations
export const useRegistrationBlockedTranslations = createTranslationHook(
  REGISTRATION_BLOCKED_TRANSLATION_CONFIG,
  'registration-blocked-page',
  processRegistrationBlockedTranslations
);

// Hook to handle phone-verification page translations
export const usePhoneVerificationTranslations = createTranslationHook(
  PHONE_VERIFICATION_TRANSLATION_CONFIG,
  'phone-verification-page',
  processPhoneVerificationTranslations
);

// Hook to handle adult-minor page translations
export const useAdultMinorTranslations = createTranslationHook(
  ADULT_MINOR_TRANSLATION_CONFIG,
  'adult-minor-page',
  processAdultMinorTranslations
);

// Hook to handle already-linked page translations
export const useAlreadyLinkedTranslations = createTranslationHook(
  ALREADY_LINKED_TRANSLATION_CONFIG,
  'already-linked-page',
  processAlreadyLinkedTranslations
);

// Hook to handle already-exists page translations
export const useAlreadyExistsTranslations = createTranslationHook(
  ALREADY_EXISTS_TRANSLATION_CONFIG,
  'already-exists-page',
  processAlreadyExistsTranslations
);

// Hook to handle confirmation page translations
export const useConfirmationTranslations = createTranslationHook(
  CONFIRMATION_TRANSLATION_CONFIG,
  'confirmation-page',
  processConfirmationTranslations
);

// Hook to handle connect-another-way page translations
export const useConnectAnotherWayTranslations = createTranslationHook(
  CONNECT_ANOTHER_WAY_TRANSLATION_CONFIG,
  'connect-another-way-page',
  processConnectAnotherWayTranslations
);

// Hook to handle locked page translations
export const useLockedTranslations = createTranslationHook(
  LOCKED_TRANSLATION_CONFIG,
  'locked-page',
  processLockedTranslations
);

// Hook to handle lockout page translations
export const useLockoutTranslations = createTranslationHook(
  LOCKOUT_TRANSLATION_CONFIG,
  'lockout-page',
  processLockoutTranslations
);

// Hook to handle success page translations
export const useSuccessTranslations = createTranslationHook(
  SUCCESS_TRANSLATION_CONFIG,
  'success-page',
  processSuccessTranslations
);

// Hook to handle rxlabel page translations
export const useRxLabelTranslations = createTranslationHook(
  RXLABEL_TRANSLATION_CONFIG,
  'rxlabel-page',
  processRxLabelTranslations
);

// Hook to handle phone-dob page translations
export const usePhoneDobTranslations = createTranslationHook(
  PHONE_DOB_TRANSLATION_CONFIG,
  'phone-dob-page',
  processPhoneDobTranslations
);

// Hook to handle mfa page translations
export const useMfaTranslations = createTranslationHook(MFA_TRANSLATION_CONFIG, 'mfa-page', processMfaTranslations);

// Hook to handle error page translations
export const useErrorPageTranslations = createTranslationHook(
  ERROR_PAGE_TRANSLATION_CONFIG,
  'error-page',
  processErrorPageTranslations
);

// Hook to handle CSR page translations
export const useCsrTranslations = createTranslationHook(CSR_TRANSLATION_CONFIG, 'csr-page', processCsrTranslations);

// Hook to handle lookup errors translations
export const useLookupErrorsTranslations = createTranslationHook(
  LOOKUP_ERRORS_TRANSLATION_CONFIG,
  'lookup-errors',
  processLookupErrorsTranslations
);

// Hook to handle lob footer component translations
export const useLobFooterTranslations = createTranslationHook(
  LOB_FOOTER_TRANSLATION_CONFIG,
  'lob-footer-component',
  processLobFooterTranslations
);
